/**
 * Role Manager — Web Admin
 * Create, edit, delete custom roles with granular permission toggles.
 * Built-in (system) roles can have permissions edited but not deleted or renamed.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Button, IconButton, Chip, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField,
  Switch, CircularProgress, Snackbar, Alert, Avatar, Divider,
  FormControlLabel, Checkbox, FormGroup, InputAdornment, Badge,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButtonGroup, ToggleButton, alpha,
} from '@mui/material';
import {
  Add, Edit, Delete, Refresh, Lock, LockOpen, Shield, CheckCircle,
  Cancel, Search, Security, Group, Settings,
} from '@mui/icons-material';
import { InvokeApi, apiList } from '../services/ApiService';
import { Config } from '../config/Config';

// ── Colour swatches ───────────────────────────────────────────────────────────
const COLOR_SWATCHES = [
  '#1a237e', '#1565c0', '#0369a1', '#0891b2', '#0f766e', '#15803d',
  '#a16207', '#c2410c', '#dc2626', '#7c3aed', '#6d28d9', '#374151',
];

// ── Permission groups displayed together ─────────────────────────────────────
const groupOrder = ['Core', 'Operations', 'Finance', 'Tracking', 'Admin'];

// ── Role Card ─────────────────────────────────────────────────────────────────
const RoleCard = ({ role, onEdit, onDelete, allPermissions }) => {
  const perms = role.permissions || [];
  const total = allPermissions.length;

  return (
    <Card sx={{
      borderRadius: 3,
      border: `2px solid ${alpha(role.color || '#1a237e', 0.2)}`,
      boxShadow: `0 2px 16px ${alpha(role.color || '#1a237e', 0.08)}`,
      transition: 'all 0.2s',
      '&:hover': {
        boxShadow: `0 6px 28px ${alpha(role.color || '#1a237e', 0.18)}`,
        transform: 'translateY(-2px)',
      },
      position: 'relative',
      overflow: 'visible',
    }}>
      {/* System badge */}
      {role.is_system && (
        <Box sx={{
          position: 'absolute', top: -10, right: 14,
          bgcolor: '#fff', border: `1.5px solid ${alpha(role.color, 0.4)}`,
          borderRadius: 99, px: 1.2, py: 0.2, display: 'flex', alignItems: 'center', gap: 0.5,
        }}>
          <Lock sx={{ fontSize: 11, color: role.color }} />
          <Typography fontSize={10} fontWeight={700} sx={{ color: role.color, letterSpacing: 0.4 }}>
            BUILT-IN
          </Typography>
        </Box>
      )}

      <CardContent sx={{ p: 2.5 }}>
        {/* Header row */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{
              width: 44, height: 44, borderRadius: 2,
              bgcolor: alpha(role.color, 0.15),
              color: role.color,
              fontSize: 20, fontWeight: 800,
              border: `2px solid ${alpha(role.color, 0.3)}`,
            }}>
              <Shield fontSize="inherit" />
            </Avatar>
            <Box>
              <Typography fontWeight={800} fontSize={16} color="#1e293b">{role.label}</Typography>
              <Typography fontSize={11} color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                key: {role.key}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Edit role">
              <IconButton size="small" onClick={() => onEdit(role)}
                sx={{ bgcolor: alpha(role.color, 0.1), color: role.color, '&:hover': { bgcolor: alpha(role.color, 0.2) } }}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            {!role.is_system && (
              <Tooltip title="Delete role">
                <IconButton size="small" onClick={() => onDelete(role)}
                  sx={{ bgcolor: '#fef2f2', color: '#dc2626', '&:hover': { bgcolor: '#fee2e2' } }}>
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Description */}
        {role.description && (
          <Typography fontSize={13} color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.5 }}>
            {role.description}
          </Typography>
        )}

        {/* Permission count bar */}
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography fontSize={11} fontWeight={600} color="text.secondary">Permissions</Typography>
            <Typography fontSize={11} fontWeight={700} sx={{ color: role.color }}>
              {perms.length} / {total}
            </Typography>
          </Box>
          <Box sx={{ height: 5, borderRadius: 99, bgcolor: alpha(role.color, 0.12), overflow: 'hidden' }}>
            <Box sx={{
              height: '100%', borderRadius: 99,
              bgcolor: role.color,
              width: `${total > 0 ? (perms.length / total) * 100 : 0}%`,
              transition: 'width 0.4s ease',
            }} />
          </Box>
        </Box>

        {/* Permission chips */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
          {perms.slice(0, 6).map(p => {
            const perm = allPermissions.find(ap => ap.key === p);
            return (
              <Chip
                key={p}
                label={perm?.label || p}
                size="small"
                icon={<CheckCircle sx={{ fontSize: '12px !important', color: `${role.color} !important` }} />}
                sx={{
                  height: 22, fontSize: 10, fontWeight: 600,
                  bgcolor: alpha(role.color, 0.1),
                  color: role.color,
                  border: `1px solid ${alpha(role.color, 0.2)}`,
                  '& .MuiChip-icon': { ml: '6px' },
                }}
              />
            );
          })}
          {perms.length > 6 && (
            <Chip
              label={`+${perms.length - 6} more`}
              size="small"
              sx={{
                height: 22, fontSize: 10, fontWeight: 600,
                bgcolor: '#f1f5f9', color: '#64748b',
              }}
            />
          )}
          {perms.length === 0 && (
            <Typography fontSize={12} color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No permissions assigned
            </Typography>
          )}
        </Box>

        {/* Footer: employee count */}
        <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Group sx={{ fontSize: 14, color: '#94a3b8' }} />
            <Typography fontSize={12} color="text.secondary" fontWeight={500}>
              {role.employee_count ?? 0} employee{role.employee_count !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <Chip
            label={role.is_active ? 'Active' : 'Inactive'}
            size="small"
            sx={{
              height: 20, fontSize: 10, fontWeight: 700,
              bgcolor: role.is_active ? '#dcfce7' : '#f1f5f9',
              color: role.is_active ? '#15803d' : '#64748b',
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

// ── Add / Edit Dialog ─────────────────────────────────────────────────────────
const RoleDialog = ({ open, onClose, onSave, editing, allPermissions, saving }) => {
  const [form, setForm] = useState({
    key: '', label: '', description: '', color: '#1a237e', permissions: [],
  });

  useEffect(() => {
    if (editing) {
      setForm({
        key: editing.key || '',
        label: editing.label || '',
        description: editing.description || '',
        color: editing.color || '#1a237e',
        permissions: editing.permissions || [],
      });
    } else {
      setForm({ key: '', label: '', description: '', color: '#1a237e', permissions: [] });
    }
  }, [editing, open]);

  const togglePerm = (key) => {
    setForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter(p => p !== key)
        : [...prev.permissions, key],
    }));
  };

  const toggleGroup = (group, permsInGroup) => {
    const groupKeys = permsInGroup.map(p => p.key);
    const allChecked = groupKeys.every(k => form.permissions.includes(k));
    setForm(prev => ({
      ...prev,
      permissions: allChecked
        ? prev.permissions.filter(p => !groupKeys.includes(p))
        : [...new Set([...prev.permissions, ...groupKeys])],
    }));
  };

  const selectAll = () => {
    setForm(prev => ({ ...prev, permissions: allPermissions.map(p => p.key) }));
  };

  const clearAll = () => {
    setForm(prev => ({ ...prev, permissions: [] }));
  };

  // Auto-generate key from label (new roles only)
  const handleLabelChange = (val) => {
    const updates = { label: val };
    if (!editing) {
      updates.key = val.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    }
    setForm(prev => ({ ...prev, ...updates }));
  };

  const grouped = groupOrder.map(g => ({
    group: g,
    perms: allPermissions.filter(p => p.group === g),
  })).filter(g => g.perms.length > 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}>
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${form.color}15, ${form.color}05)`,
        borderBottom: '1px solid #f1f5f9',
        display: 'flex', alignItems: 'center', gap: 1.5, pb: 2,
      }}>
        <Avatar sx={{ bgcolor: alpha(form.color, 0.15), color: form.color, width: 36, height: 36, borderRadius: 1.5 }}>
          <Shield fontSize="small" />
        </Avatar>
        <Box>
          <Typography fontWeight={800} fontSize={17} color="#1e293b">
            {editing ? `Edit Role: ${editing.label}` : 'Create New Role'}
          </Typography>
          <Typography fontSize={12} color="text.secondary">
            {editing ? 'Update role settings and permissions' : 'Define a new role with custom permissions'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, px: 3 }}>
        <Grid container spacing={2.5}>
          {/* Role Name */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" label="Role Name *"
              value={form.label}
              onChange={e => handleLabelChange(e.target.value)}
              helperText="Displayed name for this role"
            />
          </Grid>

          {/* Role Key */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" label="Role Key *"
              value={form.key}
              onChange={e => !editing && setForm(prev => ({ ...prev, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))}
              disabled={!!editing || editing?.is_system}
              helperText={editing ? 'Key cannot be changed after creation' : 'Lowercase, no spaces (auto-filled)'}
              InputProps={{
                startAdornment: <InputAdornment position="start">
                  <Typography fontSize={12} color="text.secondary">key:</Typography>
                </InputAdornment>,
              }}
              sx={{ '& input': { fontFamily: 'monospace' } }}
            />
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              fullWidth size="small" label="Description (optional)"
              value={form.description}
              onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
              multiline rows={2}
              placeholder="Brief description of what this role can do..."
            />
          </Grid>

          {/* Colour */}
          <Grid item xs={12}>
            <Typography fontSize={13} fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
              Role Colour
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              {COLOR_SWATCHES.map(c => (
                <Box
                  key={c}
                  onClick={() => setForm(prev => ({ ...prev, color: c }))}
                  sx={{
                    width: 28, height: 28, borderRadius: '50%', bgcolor: c,
                    cursor: 'pointer',
                    border: form.color === c ? `3px solid #000` : '3px solid transparent',
                    outline: form.color === c ? `2px solid ${c}` : 'none',
                    outlineOffset: 2,
                    transition: 'all 0.15s',
                    '&:hover': { transform: 'scale(1.15)' },
                  }}
                />
              ))}
              {/* Custom hex */}
              <TextField
                size="small"
                value={form.color}
                onChange={e => setForm(prev => ({ ...prev, color: e.target.value }))}
                sx={{ width: 120 }}
                inputProps={{ maxLength: 7 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: form.color, border: '1px solid #e2e8f0' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>

          {/* Permissions */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography fontSize={13} fontWeight={700} color="#1e293b">
                Permissions
                <Chip
                  label={`${form.permissions.length} / ${allPermissions.length} selected`}
                  size="small"
                  sx={{ ml: 1, height: 20, fontSize: 10, fontWeight: 700, bgcolor: alpha(form.color, 0.12), color: form.color }}
                />
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75 }}>
                <Button size="small" onClick={selectAll} sx={{ fontSize: 11, color: form.color, minWidth: 'unset' }}>
                  Select all
                </Button>
                <Typography color="text.disabled" sx={{ mt: 0.4 }}>·</Typography>
                <Button size="small" onClick={clearAll} sx={{ fontSize: 11, color: '#94a3b8', minWidth: 'unset' }}>
                  Clear all
                </Button>
              </Box>
            </Box>

            <Box sx={{
              border: '1.5px solid #e2e8f0', borderRadius: 2, overflow: 'hidden',
              background: '#fafbff',
            }}>
              {grouped.map(({ group, perms }, gi) => {
                const groupKeys = perms.map(p => p.key);
                const allChecked = groupKeys.every(k => form.permissions.includes(k));
                const someChecked = groupKeys.some(k => form.permissions.includes(k));

                return (
                  <Box key={group}>
                    {gi > 0 && <Divider />}
                    {/* Group header */}
                    <Box sx={{
                      px: 2, py: 1.2,
                      bgcolor: allChecked ? alpha(form.color, 0.06) : '#f8fafc',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={allChecked}
                              indeterminate={!allChecked && someChecked}
                              onChange={() => toggleGroup(group, perms)}
                              sx={{
                                color: form.color,
                                '&.Mui-checked': { color: form.color },
                                '&.MuiCheckbox-indeterminate': { color: form.color },
                              }}
                            />
                          }
                          label={
                            <Typography fontSize={13} fontWeight={700} color="#374151">
                              {group}
                            </Typography>
                          }
                          sx={{ m: 0 }}
                        />
                      </Box>
                      <Typography fontSize={11} color="text.secondary">
                        {groupKeys.filter(k => form.permissions.includes(k)).length}/{perms.length}
                      </Typography>
                    </Box>

                    {/* Permission checkboxes */}
                    <Box sx={{ px: 2, py: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {perms.map(perm => {
                        const checked = form.permissions.includes(perm.key);
                        return (
                          <Box
                            key={perm.key}
                            onClick={() => togglePerm(perm.key)}
                            sx={{
                              display: 'flex', alignItems: 'center', gap: 0.75,
                              px: 1.5, py: 0.6, borderRadius: 99, cursor: 'pointer',
                              bgcolor: checked ? alpha(form.color, 0.12) : '#fff',
                              border: `1.5px solid ${checked ? alpha(form.color, 0.4) : '#e2e8f0'}`,
                              transition: 'all 0.15s',
                              '&:hover': { bgcolor: checked ? alpha(form.color, 0.18) : '#f8fafc' },
                              userSelect: 'none',
                            }}
                          >
                            {checked
                              ? <CheckCircle sx={{ fontSize: 14, color: form.color }} />
                              : <Cancel sx={{ fontSize: 14, color: '#cbd5e1' }} />
                            }
                            <Typography fontSize={12} fontWeight={checked ? 700 : 500}
                              sx={{ color: checked ? form.color : '#64748b' }}>
                              {perm.label}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, borderTop: '1px solid #f1f5f9', gap: 1 }}>
        <Button onClick={onClose} sx={{ borderRadius: 2, color: '#64748b', fontWeight: 600 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onSave(form)}
          disabled={saving || !form.label || !form.key}
          sx={{
            borderRadius: 2, fontWeight: 700, minWidth: 130,
            bgcolor: form.color,
            '&:hover': { bgcolor: alpha(form.color, 0.85) },
            '&.Mui-disabled': { opacity: 0.6 },
          }}
        >
          {saving
            ? <CircularProgress size={18} sx={{ color: '#fff' }} />
            : editing ? 'Save Changes' : 'Create Role'
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Delete Confirm Dialog ─────────────────────────────────────────────────────
const DeleteDialog = ({ open, role, onClose, onConfirm, deleting }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
    PaperProps={{ sx: { borderRadius: 3 } }}>
    <DialogContent sx={{ pt: 4, pb: 2, textAlign: 'center' }}>
      <Avatar sx={{ width: 56, height: 56, bgcolor: '#fef2f2', mx: 'auto', mb: 2 }}>
        <Delete sx={{ color: '#dc2626', fontSize: 28 }} />
      </Avatar>
      <Typography fontWeight={800} fontSize={17} color="#1e293b" sx={{ mb: 1 }}>
        Delete "{role?.label}"?
      </Typography>
      <Typography fontSize={13} color="text.secondary" sx={{ lineHeight: 1.6 }}>
        This will permanently delete the role. Employees using this role must be reassigned first.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 3, gap: 1, justifyContent: 'center' }}>
      <Button onClick={onClose} variant="outlined"
        sx={{ borderRadius: 2, fontWeight: 600, minWidth: 100 }}>
        Cancel
      </Button>
      <Button onClick={onConfirm} variant="contained" disabled={deleting}
        sx={{ borderRadius: 2, fontWeight: 700, minWidth: 100, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}>
        {deleting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Delete'}
      </Button>
    </DialogActions>
  </Dialog>
);

// ── Main RoleManager Page ─────────────────────────────────────────────────────
export default function RoleManager() {
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    const res = await InvokeApi(Config.base_url + apiList.getRoles, {});
    if (res?.code === '200') {
      setRoles(res.roles || []);
      setAllPerms(res.permissions || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditRole(null); setDialogOpen(true); };
  const openEdit = (role) => { setEditRole(role); setDialogOpen(true); };

  const handleSave = async (form) => {
    if (!form.label.trim()) { setSnack({ open: true, msg: 'Role name is required.', sev: 'warning' }); return; }
    if (!form.key.trim()) { setSnack({ open: true, msg: 'Role key is required.', sev: 'warning' }); return; }

    setSaving(true);
    const endpoint = editRole ? apiList.editRole : apiList.addRole;
    const payload = editRole
      ? { id: editRole.id, label: form.label, description: form.description, color: form.color, permissions: form.permissions }
      : { key: form.key, label: form.label, description: form.description, color: form.color, permissions: form.permissions };

    const res = await InvokeApi(Config.base_url + endpoint, payload);
    setSaving(false);

    if (res?.code === '200') {
      setSnack({ open: true, msg: editRole ? 'Role updated successfully.' : 'Role created successfully.', sev: 'success' });
      setDialogOpen(false);
      load(true);
    } else {
      setSnack({ open: true, msg: res?.message || 'Failed to save role.', sev: 'error' });
    }
  };

  const openDelete = (role) => { setDeleteTarget(role); setDeleteDialog(true); };
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const res = await InvokeApi(Config.base_url + apiList.deleteRole, { id: deleteTarget.id });
    setDeleting(false);
    if (res?.code === '200') {
      setSnack({ open: true, msg: 'Role deleted.', sev: 'success' });
      setDeleteDialog(false);
      load(true);
    } else {
      setSnack({ open: true, msg: res?.message || 'Cannot delete this role.', sev: 'error' });
    }
  };

  // Filter
  const filtered = roles.filter(r => {
    const matchSearch = !search || r.label.toLowerCase().includes(search.toLowerCase()) || r.key.includes(search.toLowerCase());
    const matchType = filterType === 'all'
      ? true
      : filterType === 'system' ? r.is_system
        : filterType === 'custom' ? !r.is_system
          : filterType === 'active' ? r.is_active
            : !r.is_active;
    return matchSearch && matchType;
  });

  const systemCount = roles.filter(r => r.is_system).length;
  const customCount = roles.filter(r => !r.is_system).length;
  const totalPerms = allPermissions.length;

  return (
    <Box>
      {/* ── Page header ──────────────────────────────────────────────────── */}
      {/* <Box sx={{
        mb: 3, p: 3, borderRadius: 3,
        background: 'linear-gradient(135deg, #1a237e 0%, #1565c0 60%, #0369a1 100%)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 48, height: 48, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2 }}>
            <Security sx={{ fontSize: 26, color: '#fff' }} />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#fff">Role Manager</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.2 }}>
              Manage access roles and permissions across the platform
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openAdd}
          sx={{
            bgcolor: 'rgba(255,255,255,0.2)',
            color: '#fff',
            fontWeight: 700,
            border: '1.5px solid rgba(255,255,255,0.4)',
            borderRadius: 2,
            backdropFilter: 'blur(8px)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
          }}
        >
          Create Role
        </Button>
      </Box> */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Role Manager</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage access roles and permissions across the platform
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* <Tooltip title="Refresh">
            <IconButton onClick={() => fetchEmployees()} sx={{ color: '#1a237e', border: '1px solid #e8eaf6', borderRadius: 2 }}>
              <Refresh />
            </IconButton>
          </Tooltip> */}
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openAdd}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, px: 3 }}
          >
            Create Role
          </Button>
        </Box>
      </Box>

      {/* ── Stats strip ──────────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Roles', val: roles.length, color: '#1a237e', icon: Shield },
          { label: 'Built-in', val: systemCount, color: '#64748b', icon: Lock },
          { label: 'Custom', val: customCount, color: '#7c3aed', icon: LockOpen },
          { label: 'Permissions', val: totalPerms, color: '#0891b2', icon: Settings },
        ].map(({ label, val, color, icon: Icon }) => (
          <Grid item xs={6} sm={3} key={label}>
            <Card sx={{
              borderRadius: 2.5, border: `1px solid ${alpha(color, 0.2)}`,
              bgcolor: alpha(color, 0.05),
              boxShadow: 'none',
            }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: alpha(color, 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon sx={{ fontSize: 18, color }} />
                  </Box>
                  <Box>
                    <Typography fontWeight={800} fontSize={20} color={color}>{val}</Typography>
                    <Typography fontSize={11} color="text.secondary" fontWeight={500}>{label}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <Card sx={{ borderRadius: 2.5, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', mb: 3 }}>
        <CardContent sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search roles…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
            }}
            sx={{ flex: 1, minWidth: 240 }}
          />

          <ToggleButtonGroup
            value={filterType}
            exclusive
            onChange={(_, v) => { if (v) setFilterType(v); }}
            size="small"
            sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.6, fontSize: 12, fontWeight: 600, textTransform: 'none', border: '1px solid #e2e8f0' } }}
          >
            <ToggleButton value="all">All ({roles.length})</ToggleButton>
            <ToggleButton value="system">Built-in</ToggleButton>
            <ToggleButton value="custom">Custom</ToggleButton>
            <ToggleButton value="active">Active</ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title="Refresh">
            <IconButton onClick={() => load()} sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, color: '#1a237e' }}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </CardContent>
      </Card>

      {/* ── Role Cards Grid ───────────────────────────────────────────────── */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#1a237e' }} />
        </Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <Shield sx={{ fontSize: 64, color: '#e2e8f0', mb: 2 }} />
          <Typography fontWeight={700} color="text.secondary">No roles found</Typography>
          <Typography variant="caption" color="text.disabled">Try adjusting your search or filter</Typography>
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" startIcon={<Add />} onClick={openAdd}
              sx={{ borderRadius: 2, bgcolor: '#1a237e', fontWeight: 700 }}>
              Create First Role
            </Button>
          </Box>
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          {filtered.map(role => (
            <Grid item xs={12} sm={6} lg={4} key={role.id}>
              <RoleCard
                role={role}
                onEdit={openEdit}
                onDelete={openDelete}
                allPermissions={allPermissions}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Dialogs ───────────────────────────────────────────────────────── */}
      <RoleDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        editing={editRole}
        allPermissions={allPermissions}
        saving={saving}
      />

      <DeleteDialog
        open={deleteDialog}
        role={deleteTarget}
        onClose={() => setDeleteDialog(false)}
        onConfirm={handleDelete}
        deleting={deleting}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
